import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Button,
  Paper,
  Box,
  Alert,
  CircularProgress
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { doc, setDoc, serverTimestamp } from 'firebase/firestore';
import { db, auth } from '../firebase';
import GoogleIcon from '@mui/icons-material/Google';
import bgImage from '../assets/bg.svg';
import ReCaptcha from "../components/ReCaptcha";

const SignupPage = () => {
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { loginWithGoogle, isAuthenticated, user } = useAuth();
  const navigate = useNavigate();
  const [captchaVerified, setCaptchaVerified] = useState(false);

  const handleCaptchaVerify = (status) => {
    setCaptchaVerified(status);
  };

  useEffect(() => {
    if (isAuthenticated && user) {
      navigate(user.role === 'host' || user.role === 'tenant' ? '/host-dashboard' : '/visitor-dashboard');
    }
  }, [isAuthenticated, user, navigate]);

  const handleGoogleSignup = async () => {
    if (loading) return;
    setLoading(true);
    setError('');

    try {
      const result = await loginWithGoogle(true);
      if (result.user) {
        await setDoc(doc(db, 'users', result.user.uid), {
          userID: result.user.uid,
          userName: result.user.displayName || 'New Visitor',
          userEmailID: result.user.email,
          userMobileNo: null,
          companyName: null,
          role: 'visitor',
          createdAt: serverTimestamp()
        });
        window.location.href = '/visitor-dashboard';
      }
    } catch (error) {
      console.error('Google Sign-Up Error:', error);
      setError(error.message || 'Failed to sign up');
      await auth.signOut();
    } finally {
      setLoading(false);
    }
  };

  const styles = {

    nav: {
      position: "fixed",
      top: 0,
      left: 0,
      right: 0,
      zIndex: 100,
      height: "64px",
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      p: "0 5vw",
      background: "rgba(254,252,232,.94)",
      backdropFilter: "blur(14px)",
      borderBottom: "2px solid #f0d060"
    },

    logo: {
      fontSize: "1.2rem",
      fontWeight: 800,
      color: "#1c1400",
      display: "flex",
      alignItems: "center"
    },

    unbounded: {
      fontFamily: "'Unbounded', sans-serif"
    },

    loginWrap: {
      display: "grid",
      gridTemplateColumns: "1fr 1fr",
      minHeight: "calc(100vh - 70px)",

      "@media (max-width: 900px)": {
        gridTemplateColumns: "1fr"
      }
    }

  };

  return (
    <Box sx={{ minHeight: "100vh", backgroundColor: "#fff" }}>

      {/* NAVBAR */}
      <Box sx={styles.nav}>
        <Typography sx={{ ...styles.logo, ...styles.unbounded }}>
          AALAY
          <Box component="span" sx={{
            display: "inline-block",
            width: "8px",
            height: "8px",
            borderRadius: "50%",
            backgroundColor: "#f59e0b",
            ml: 0.5,
            verticalAlign: "middle"
          }} />
        </Typography>

        <Box sx={{ display: "flex", gap: 2 }}>
          {/* <Button sx={{
                  p: "8px 18px",
                  borderRadius: "6px",
                  border: "2px solid #f0d60",
                  background: "transparent",
                  color: "#3d2e00",
                  fontSize: "0.8rem",
                  fontWeight: 500,
                  "&:hover": {
                    borderColor: "#f59e0b",
                    color: "#ca8a04"
                  }
      
                }}
                  onClick={() => navigate("/login")}>
                  Sign in
                </Button> */}
          <Button sx={{
            p: "9px 22px",
            borderRadius: "6px",
            backgroundColor: "#1c1400",
            border: "2px solid #1c1400",
            color: "#fde047",
            fontSize: "0.75rem",
            fontWeight: 700,
            letterSpacing: "-0.02em",
            fontFamily: "'Unbounded', sans-serif",
            "&:hover": {
              backgroundColor: "#f59e0b",
              borderColor: "#f59e0b",
              color: "#1c1400"
            }
          }}
            onClick={() => navigate("/")}>
            BACK TO HOME ←
          </Button>
        </Box>
      </Box>

      {/* MAIN */}
      <Box sx={{
        display: "grid",
        gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
        minHeight: "calc(100vh - 70px)"
      }}>

        {/* LEFT PANEL */}
        <Box sx={{
          backgroundColor: "#fde047",
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          p: { xs: "40px 7vw", md: "80px 7vw" },
          position: "relative",
          overflow: "hidden",
          borderRight: "2px solid #f0d060"
        }}>

          <Box sx={{
            position: "absolute",
            width: "300px",
            height: "300px",
            borderRadius: "50%",
            border: "40px solid rgba(202,138,4,.2)",
            bottom: "-100px",
            right: "-80px"
          }} />

          <Box sx={{ position: "relative", zIndex: 1 }}>

            <Typography sx={{
              ...styles.unbounded,
              fontSize: { xs: "2rem", md: "2.5rem" },
              fontWeight: 900,
              color: "#1c1400",
              lineHeight: 1.05,
              letterSpacing: "-0.04em",
              mb: 2
            }}>
              CREATE<br /> YOUR<br />VISITOR ACCOUNT.
            </Typography>

            <Typography sx={{
              color: "#3d2e00",
              fontSize: "0.9rem",
              lineHeight: 1.65,
              maxWidth: "320px",
              fontWeight: 300,
              mb: 5
            }}>
              Manage visitors, residents and facility operations
              securely from one centralized platform.
            </Typography>

            {/* STATS */}
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>

              <Paper sx={{ p: 2, borderRadius: "10px" }}>
                {/* <Typography sx={{ fontSize: "0.7rem" }}>
                                VISITORS MANAGED
                              </Typography> */}
                <Typography sx={{ fontWeight: 900 }}>
                  Visitor Management
                </Typography>
              </Paper>

              <Paper sx={{ p: 2, borderRadius: "10px" }}>
                {/* <Typography sx={{ fontSize: "0.7rem" }}>
                                ACTIVE FACILITIES
                              </Typography> */}
                <Typography sx={{ fontWeight: 900 }}>
                  Society Management
                </Typography>
              </Paper>

              <Paper sx={{ p: 2, borderRadius: "10px" }}>
                {/* <Typography sx={{ fontSize: "0.7rem" }}>
                                SYSTEM UPTIME
                              </Typography> */}
                <Typography sx={{ fontWeight: 900 }}>
                  Intelligent Operations
                </Typography>
              </Paper>

              <Paper sx={{ p: 2, borderRadius: "10px" }}>
                {/* <Typography sx={{ fontSize: "0.7rem" }}>
                                SYSTEM UPTIME
                              </Typography> */}
                <Typography sx={{ fontWeight: 900 }}>
                  Templates Management
                </Typography>
              </Paper>

            </Box>
          </Box>
        </Box>

        {/* RIGHT PANEL */}
        <Box sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          p: "60px 6vw"
        }}>

          <Box sx={{ width: "100%", maxWidth: 400 }}>

            <Typography sx={{
              fontFamily: "'Unbounded', sans-serif",
              fontSize: "1.6rem",
              fontWeight: 900,
              mb: 1
            }}>
              GET STARTED.
            </Typography>

            <Typography sx={{ mb: 3 }}>
              Create your VISIT account to begin.
            </Typography>

            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}

            {/* RECAPTCHA */}
            <Box sx={{ mb: 3 }}>
              <ReCaptcha onVerify={handleCaptchaVerify} />
            </Box>

            {/* GOOGLE SIGNUP */}
            <Button
              fullWidth
              onClick={captchaVerified ? handleGoogleSignup : null}
              disabled={!captchaVerified || loading}
              sx={{
                p: "13px",
                borderRadius: "8px",
                border: "2px solid #f0d060",
                backgroundColor: "#fefce8",
                color: "#1c1400",
                mb: 3
              }}
            >
              <img
                width="24"
                height="24"
                src="https://img.icons8.com/color/48/google-logo.png"
                alt="google"
                style={{ marginRight: 10 }}
              />
              {loading ? (
                <CircularProgress size={22} />
              ) : (
                "Continue with Google"
              )}
            </Button>

            {/* EMAIL SIGNUP UI
        <Paper sx={{
          p: 3,
          borderRadius: "10px",
          border: "2px solid #f0d060"
        }}>
          <Typography sx={{
            fontFamily: "'Unbounded', sans-serif",
            fontSize: "0.75rem",
            fontWeight: 700,
            textAlign: "center",
            mb: 2
          }}>
            QUICK SIGNUP
          </Typography>

          <input
            type="email"
            placeholder="you@company.com"
            style={{
              width: "100%",
              padding: "12px",
              marginBottom: "10px",
              borderRadius: "6px",
              border: "1px solid #ccc"
            }}
          />

          <Button fullWidth sx={{
            backgroundColor: "#1c1400",
            color: "#fde047"
          }}>
            CONTINUE
          </Button>
        </Paper> */}

            <Typography sx={{ mt: 3, textAlign: "center" }}>
              Already have an account?{" "}
              <span
                style={{ cursor: "pointer", fontWeight: 600 }}
                onClick={() => navigate("/login")}
              >
                Log in
              </span>
            </Typography>

          </Box>
        </Box>

      </Box>

    </Box>
  );
};

export default SignupPage;
